import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, RefreshCw, Play, Pause, CheckCircle2, Circle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useFetch } from '@/hooks/useFetch';
import { useApplicants } from '@/contexts/ApplicantContext';
import { useRecentJobs } from '@/hooks/useRecentJobs';
import { Card, Kpi } from '@/components/ui/Card';
import { WorkerHealth } from '@/components/WorkerHealth';
import { relativeTime } from '@/lib/format';
import { api, ApiError } from '@/services/api';
import type { DashboardData } from '@/services/types';

interface FunnelData {
  stages: { key: string; label: string; count: number }[];
}

interface FailureReasonsData {
  reasons: { reason: string; count: number }[];
}

interface DueReminder {
  id: number;
  company: string | null;
  title: string | null;
  remind_at: string;
  remind_message: string | null;
}

interface AutonomyHealth {
  queue_pending: number;
  last_apply_at: string | null;
  last_attempt_at: string | null;
  online_workers: number;
  alert: 'ok' | 'stalled';
  reason: string | null;
}

export default function DashboardPage() {
  const { current, refresh: refreshApplicants } = useApplicants();
  const recent = useRecentJobs();
  // Auto-refresh every 30s by default — interval is hidden from main UI per
  // Peter's "extremely concise" direction. Tweakable via localStorage if needed.
  const REFRESH_SEC = 30;
  const { data, loading, error, refresh } = useFetch<DashboardData>(`/dashboard?applicant_id=${current}&days=30&limit=20`);
  const funnelQ = useFetch<FunnelData>(`/dashboard/funnel?applicant_id=${current}`);
  const failureQ = useFetch<FailureReasonsData>(`/dashboard/failure-reasons?applicant_id=${current}&limit=8`);
  const remindersQ = useFetch<{ items: DueReminder[] }>(`/reminders?applicant_id=${current}`);
  const autonomyQ = useFetch<AutonomyHealth>(`/dashboard/autonomy?applicant_id=${current}`);
  const profileQ = useFetch<{ score: number; missing: string[]; filled_fields: number; total_fields: number }>(
    `/resumes/profile/completeness?applicant_id=${current}`,
  );
  const hardApplyQ = useFetch<{
    queue_depth: number;
    applied_today: number;
    home_worker_online: boolean;
    home_worker_last_seen_ms_ago: number | null;
    home_worker_id: string | null;
  }>(`/hard-apply/status`);

  useEffect(() => {
    const h = setInterval(refresh, REFRESH_SEC * 1000);
    return () => clearInterval(h);
  }, [refresh]);

  if (loading && !data) {
    return (
      <div className="grid place-items-center py-20 text-gray-400">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }
  if (error) {
    return <div className="card p-4 text-sm text-red-700">Failed to load dashboard: {error}</div>;
  }
  if (!data) return null;

  const me = data.applicants.find((a) => a.applicant_id === current) ?? data.applicants[0];
  const kpis = me?.kpis;

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-semibold tracking-tight">
            {me?.display_name ?? 'Dashboard'}
          </h2>
          <p className="text-sm text-gray-500">
            {me?.email} · daily cap {me?.daily_cap} {me?.paused === 1 && '· paused'}
          </p>
        </div>
        <button onClick={refresh} className="btn-secondary" title="Refresh now">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </header>

      <ReadinessCard
        applicantId={current}
        paused={me?.paused === 1}
        profileComplete={(profileQ.data?.score ?? 0) >= 70}
        profileMissing={profileQ.data?.missing ?? []}
        onChange={() => {
          refresh();
          refreshApplicants();
        }}
      />

      {hardApplyQ.data && (hardApplyQ.data.queue_depth > 0 || hardApplyQ.data.home_worker_online) && (
        <HardApplyPanel data={hardApplyQ.data} />
      )}

      {autonomyQ.data?.alert === 'stalled' && (
        <div className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm dark:border-red-900/60 dark:bg-red-950/30">
          <div className="font-semibold text-red-800 dark:text-red-200">
            ⚠️ Autonomy stalled — {autonomyQ.data.reason}
          </div>
          <div className="mt-1 text-xs text-red-700/80 dark:text-red-300/70">
            Queue pending: {autonomyQ.data.queue_pending} · Online workers: {autonomyQ.data.online_workers}
            {autonomyQ.data.last_apply_at && ' · Last successful apply: ' + relativeTime(autonomyQ.data.last_apply_at)}
          </div>
        </div>
      )}

      <WorkerHealth />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Kpi label="Applied today" value={kpis?.applied_today ?? 0} />
        <Kpi label="This week" value={kpis?.applied_this_week ?? 0} />
        <Kpi label="This month" value={kpis?.applied_this_month ?? 0} />
        <Kpi label="Queue depth" value={kpis?.queue_depth ?? 0} hint="jobs awaiting an attempt" />
        <Kpi
          label="Success rate"
          value={`${kpis?.success_rate ?? 0}%`}
          tone={(kpis?.success_rate ?? 0) >= 40 ? 'good' : (kpis?.success_rate ?? 0) >= 25 ? 'warn' : 'bad'}
        />
        <Kpi label="Failed total" value={kpis?.failed_total ?? 0} tone="bad" />
        <Kpi label="Interviews" value={kpis?.interviews ?? 0} tone="good" hint="undismissed signals" />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="Recent activity" className="lg:col-span-2">
          <ActivityFeed items={data.activity} />
        </Card>
        <Card title="Top companies">
          <TopCompanies rows={data.top_companies.slice(0, 8)} />
        </Card>
      </div>

      {remindersQ.data && remindersQ.data.items.length > 0 && (
        <Card title={`Due follow-ups (${remindersQ.data.items.length})`}>
          <ul className="space-y-1 text-sm">
            {remindersQ.data.items.map((r) => (
              <li key={r.id} className="flex items-center gap-2">
                <Link
                  to={`/applications/${r.id}`}
                  className="flex-1 truncate text-primary-700 hover:underline dark:text-primary-300"
                >
                  {r.company ?? '—'} · {r.title ?? ''}
                </Link>
                {r.remind_message && (
                  <span className="hidden truncate text-xs text-gray-500 md:inline">
                    {r.remind_message}
                  </span>
                )}
                <span className="shrink-0 text-xs text-amber-600 dark:text-amber-400">
                  {relativeTime(r.remind_at)}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <details className="rounded-lg border border-gray-200 dark:border-gray-800">
        <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-200">
          Details — funnel · failures
        </summary>
        <div className="space-y-4 border-t border-gray-100 p-4 dark:border-gray-800">
          {funnelQ.data && <Card title="Conversion funnel"><Funnel stages={funnelQ.data.stages} /></Card>}
          {failureQ.data && failureQ.data.reasons.length > 0 && (
            <Card title="Failure breakdown">
              <FailureReasons rows={failureQ.data.reasons} />
            </Card>
          )}
        </div>
      </details>

      {recent.length > 0 && (
        <Card title="Recently viewed">
          <ul className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
            {recent.slice(0, 6).map((r) => (
              <li key={r.id}>
                <Link
                  to={`/applications/${r.id}`}
                  className="block rounded-md border border-gray-200 px-3 py-2 text-sm hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800"
                >
                  <div className="font-medium text-gray-800 dark:text-gray-100">{r.company ?? '—'}</div>
                  <div className="truncate text-xs text-gray-500">{r.title}</div>
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card title="Applied per day (last 30)">
        <TimeseriesChart days={data.timeseries} />
      </Card>
    </div>
  );
}

function HardApplyPanel({
  data,
}: {
  data: {
    queue_depth: number;
    applied_today: number;
    home_worker_online: boolean;
    home_worker_last_seen_ms_ago: number | null;
    home_worker_id: string | null;
  };
}) {
  const online = data.home_worker_online;
  const minsAgo =
    data.home_worker_last_seen_ms_ago != null
      ? Math.round(data.home_worker_last_seen_ms_ago / 60000)
      : null;

  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3 text-sm ${
        online
          ? 'border-green-200 bg-green-50 dark:border-green-900/60 dark:bg-green-950/30'
          : data.queue_depth > 0
            ? 'border-amber-200 bg-amber-50 dark:border-amber-900/60 dark:bg-amber-950/30'
            : 'border-gray-200 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/40'
      }`}
    >
      <div className="flex items-center gap-3">
        <span
          className={`inline-block h-2.5 w-2.5 rounded-full ${
            online ? 'bg-green-500 ring-2 ring-green-200' : 'bg-gray-400'
          }`}
          aria-hidden
        />
        <div>
          <div className="font-medium">
            Home worker {online ? 'online' : 'offline'}
            {data.home_worker_id && (
              <span className="ml-2 text-xs text-gray-500">{data.home_worker_id}</span>
            )}
          </div>
          <div className="text-xs text-gray-600 dark:text-gray-300">
            {data.queue_depth} job{data.queue_depth === 1 ? '' : 's'} waiting in backup queue ·{' '}
            {data.applied_today} applied today via home
            {!online && data.queue_depth > 0 && (
              <span className="ml-2 text-amber-700 dark:text-amber-300">
                — start it so these can move
              </span>
            )}
            {minsAgo != null && !online && (
              <span className="ml-2 text-gray-500">· last seen {minsAgo}m ago</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ReadinessCard({
  applicantId,
  paused,
  profileComplete,
  profileMissing,
  onChange,
}: {
  applicantId: string;
  paused: boolean;
  profileComplete: boolean;
  profileMissing: string[];
  onChange: () => void;
}) {
  const [busy, setBusy] = useState(false);

  // Readiness checklist. resume_present is implied by applicant_id existing
  // (the onboarding wizard always saves one). When profile_complete is false,
  // missing[] tells the user what to fix.
  const checks: { ok: boolean; label: string; hint?: string }[] = [
    { ok: true, label: 'Resume uploaded' },
    {
      ok: profileComplete,
      label: profileComplete ? 'Profile information' : 'Profile information incomplete',
      hint: !profileComplete && profileMissing.length > 0
        ? `Missing: ${profileMissing.slice(0, 4).join(', ')}${profileMissing.length > 4 ? '…' : ''}`
        : undefined,
    },
  ];
  const ready = checks.every((c) => c.ok);
  const isApplying = ready && !paused;

  async function toggle() {
    setBusy(true);
    try {
      const next = !paused;
      await api.post(`/applicants/${applicantId}/pause`, { paused: next });
      toast.success(next ? 'Paused — no new applies will start' : 'Applying — broker will pick up jobs');
      onChange();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className={`rounded-lg border p-4 ${
        isApplying
          ? 'border-green-200 bg-green-50 dark:border-green-900/60 dark:bg-green-950/30'
          : ready
            ? 'border-blue-200 bg-blue-50 dark:border-blue-900/60 dark:bg-blue-950/30'
            : 'border-gray-200 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/40'
      }`}
    >
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1">
          <div className="text-base font-semibold">
            {isApplying
              ? 'Applying for you'
              : ready
                ? 'Ready to apply'
                : 'Set up to start applying'}
          </div>
          <div className="text-xs text-gray-600 dark:text-gray-300">
            {isApplying
              ? 'The broker will dispatch jobs to your worker as they pass your filters.'
              : ready
                ? 'Click Start to begin auto-applying.'
                : "Finish the items below — once they're all green, you can start applying."}
          </div>
        </div>
        <button
          onClick={toggle}
          disabled={!ready || busy}
          className={
            paused
              ? 'btn-primary disabled:bg-gray-300 disabled:text-gray-500 disabled:hover:bg-gray-300'
              : 'btn-secondary'
          }
          title={
            !ready
              ? `Not ready: ${checks.filter((c) => !c.ok).map((c) => c.label).join('; ')}`
              : paused
                ? 'Start applying'
                : 'Pause applying'
          }
        >
          {busy ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : paused ? (
            <>
              <Play className="h-4 w-4" />
              Start applying
            </>
          ) : (
            <>
              <Pause className="h-4 w-4" />
              Pause
            </>
          )}
        </button>
      </div>
      <ul className="mt-3 space-y-1 text-sm">
        {checks.map((c, i) => (
          <li key={i} className="flex items-start gap-2">
            {c.ok ? (
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
            ) : (
              <Circle className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
            )}
            <div>
              <div className={c.ok ? 'text-gray-700 dark:text-gray-200' : 'text-gray-500'}>
                {c.label}
              </div>
              {c.hint && (
                <div className="flex items-center gap-1 text-xs text-amber-700 dark:text-amber-300">
                  <AlertCircle className="h-3 w-3" />
                  {c.hint}
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Funnel({ stages }: { stages: { key: string; label: string; count: number }[] }) {
  if (!stages.length) return null;
  const max = Math.max(1, ...stages.map((s) => s.count));
  return (
    <ul className="space-y-2">
      {stages.map((s, i) => {
        const pct = (s.count / max) * 100;
        const prev = i > 0 ? stages[i - 1].count : null;
        const conv = prev && prev > 0 ? Math.round((s.count / prev) * 1000) / 10 : null;
        return (
          <li key={s.key} className="space-y-0.5">
            <div className="flex items-center justify-between text-xs">
              <span className="font-medium text-gray-700 dark:text-gray-200">{s.label}</span>
              <span className="text-gray-500">
                {s.count.toLocaleString()}
                {conv != null && (
                  <span className="ml-2 text-gray-400">({conv}% from prev)</span>
                )}
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded bg-gray-100 dark:bg-gray-800">
              <div
                className="h-full rounded bg-primary-600"
                style={{ width: `${Math.max(2, pct)}%` }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function ActivityFeed({ items }: { items: DashboardData['activity'] }) {
  if (!items.length) return <p className="text-sm text-gray-500">No activity yet.</p>;
  const tone: Record<string, string> = {
    application: 'bg-primary-100 text-primary-800',
    signal: 'bg-red-100 text-red-700',
    note: 'bg-amber-100 text-amber-800',
    log: 'bg-gray-100 text-gray-700',
  };
  return (
    <ul className="space-y-2">
      {items.slice(0, 20).map((e, i) => (
        <li key={`${e.kind}-${e.ts}-${i}`} className="flex items-center gap-3 text-sm">
          <span className={`badge ${tone[e.kind] ?? tone.log}`}>{e.kind}</span>
          {e.job_id ? (
            <Link to={`/applications/${e.job_id}`} className="font-medium text-primary-700 hover:underline">
              {e.company ?? '—'}
            </Link>
          ) : (
            <span className="font-medium text-gray-700">{e.company ?? '—'}</span>
          )}
          <span className="truncate text-gray-600">
            {e.status && <em className="not-italic text-gray-400">[{e.status}] </em>}
            {e.title ?? e.body ?? ''}
          </span>
          <span className="ml-auto shrink-0 text-xs text-gray-400">{relativeTime(e.ts)}</span>
        </li>
      ))}
    </ul>
  );
}

function FailureReasons({ rows }: { rows: { reason: string; count: number }[] }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <ul className="space-y-1.5">
      {rows.map((r) => {
        const pct = (r.count / max) * 100;
        return (
          <li key={r.reason} className="space-y-0.5">
            <div className="flex items-center justify-between text-xs">
              <span className="truncate font-mono text-gray-700 dark:text-gray-300" title={r.reason}>
                {r.reason}
              </span>
              <span className="ml-2 shrink-0 text-gray-500">{r.count.toLocaleString()}</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded bg-gray-100 dark:bg-gray-800">
              <div className="h-full rounded bg-red-400" style={{ width: `${Math.max(2, pct)}%` }} />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function TopCompanies({ rows }: { rows: DashboardData['top_companies'] }) {
  if (!rows.length) return <p className="text-sm text-gray-500">No data.</p>;
  return (
    <ul className="space-y-1">
      {rows.map((r) => (
        <li key={r.company} className="flex items-center justify-between text-sm">
          <span className="truncate text-gray-700">{r.company || '(no name)'}</span>
          <span className="ml-2 shrink-0 text-xs text-gray-500">
            <span className="text-green-700">{r.applied}</span>/{r.attempts}
          </span>
        </li>
      ))}
    </ul>
  );
}

function TimeseriesChart({ days }: { days: DashboardData['timeseries'] }) {
  const max = Math.max(1, ...days.map((d) => d.applied + d.failed));
  const W = 800;
  const H = 120;
  const barW = W / days.length;
  return (
    <svg viewBox={`0 0 ${W} ${H + 18}`} className="w-full overflow-visible">
      {days.map((d, i) => {
        const total = d.applied + d.failed;
        const hT = (total / max) * H;
        const hA = (d.applied / max) * H;
        return (
          <g key={d.date} transform={`translate(${i * barW},0)`}>
            <title>{`${d.date}: applied ${d.applied}, failed ${d.failed}`}</title>
            <rect
              x={2}
              y={H - hT}
              width={Math.max(2, barW - 4)}
              height={hT - hA}
              className="fill-red-300"
            />
            <rect
              x={2}
              y={H - hA}
              width={Math.max(2, barW - 4)}
              height={hA}
              className="fill-primary-600"
            />
          </g>
        );
      })}
      <line x1={0} x2={W} y1={H} y2={H} className="stroke-gray-200" />
      <text x={0} y={H + 14} className="fill-gray-400 text-[10px]">
        {days[0]?.date}
      </text>
      <text x={W} y={H + 14} textAnchor="end" className="fill-gray-400 text-[10px]">
        {days[days.length - 1]?.date}
      </text>
    </svg>
  );
}
